import REWARD_VAULT_ABI from 'abis/RewardVault.json'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useCallback, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { Address, formatUnits } from 'viem'

export function useRewardVaultClaim(vaultContractAddress: string | undefined) {
  const { account, wagmiPublicClient } = useConnection()
  const addTransaction = useTransactionAdder()
  const { writeContractAsync } = useWriteContract()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  // Track transaction states
  const [claimState, setClaimState] = useState({
    attemptingTx: false,
    hash: '',
    error: '',
  })

  const claimRewards = useCallback(
    async (recipient: string, amount: string, rewardTokenSymbol?: string): Promise<boolean> => {
      if (!account || !vaultContractAddress || !wagmiPublicClient) {
        setClaimState({
          attemptingTx: false,
          hash: '',
          error: 'Missing dependencies for claim',
        })
        return false
      }

      try {
        setClaimState({
          attemptingTx: true,
          hash: '',
          error: '',
        })

        // Convert amount for transaction details
        const DECIMALS = 18
        const humanReadableAmount = parseFloat(formatUnits(BigInt(amount), DECIMALS))

        const txn = {
          address: vaultContractAddress as Address,
          abi: REWARD_VAULT_ABI,
          functionName: 'getReward',
          args: [account as Address, recipient as Address],
          account: account as Address,
        }

        // Simulate the contract call first
        const gas = await wagmiPublicClient.estimateContractGas(txn)

        // Send the transaction using the simulation result
        const hash = await writeContractAsync({
          ...txn,
          gas: calculateGasMargin(gas as bigint) as bigint,
        })

        // Store transaction hash
        setClaimState({
          attemptingTx: false,
          hash,
          error: '',
        })

        // Add to transaction history
        addTransaction(
          { hash },
          {
            type: TransactionType.REWARD_VAULT_REWARD_CLAIM,
            recipient,
            amount: humanReadableAmount,
            rewardVaultAddress: vaultContractAddress,
            rewardTokenSymbol,
          }
        )

        await waitForTransactionReceipt({
          hash,
        })

        return true
      } catch (error: any) {
        console.error('Error claiming rewards:', error)
        setClaimState({
          attemptingTx: false,
          hash: '',
          error: error?.reason || error?.message || 'Failed to claim rewards',
        })
        return false
      }
    },
    [account, vaultContractAddress, addTransaction, writeContractAsync, wagmiPublicClient, waitForTransactionReceipt]
  )

  const resetTxStates = useCallback(() => {
    setClaimState({
      attemptingTx: false,
      hash: '',
      error: '',
    })
  }, [])

  return {
    claimRewards,
    claimState,
    resetTxStates,
  }
}
